// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { FileUploaderDropContainer, FileUploaderItem, ProgressBar } from '@carbon/react';
import { Document } from '@carbon/icons-react';

export interface UploadingFile {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
}

const UPLOAD_ICON_DESCRIPTION: Record<UploadingFile['status'], string> = {
  pending: 'Remove file',
  uploading: 'Uploading file',
  done: 'Upload complete',
  error: 'Remove file',
};

interface UploadDropzoneProps {
  files: UploadingFile[];
  disabled?: boolean;
  onAddFiles: (_files: File[]) => void;
  onRemoveFile: (_index: number) => void;
}

/**
 * Multi-file drop zone per the design mock: any document type, no per-slot
 * uploads — files are classified automatically after upload.
 */
export default function UploadDropzone({
  files,
  disabled = false,
  onAddFiles,
  onRemoveFile,
}: UploadDropzoneProps) {
  return (
    <div className="obw-dropzone">
      <FileUploaderDropContainer
        labelText="Drag and drop documents here, or click to upload"
        multiple
        disabled={disabled}
        accept={['.pdf', '.docx', '.xlsx', '.csv']}
        onAddFiles={(_event: unknown, { addedFiles }: { addedFiles: File[] }) =>
          onAddFiles(addedFiles)
        }
      />
      <p className="obw-dropzone-hint">
        Any document type. Files are classified automatically; there are no per-slot uploads. PDF,
        DOCX, XLSX, CSV accepted. Bundles are split by page range.
      </p>
      {files.map((entry, index) => (
        <div key={`${entry.file.name}-${index}`} className="obw-upload-item">
          <div className="obw-upload-row">
            {/* A staged file hasn't been sent anywhere yet — Carbon's
                'uploading' status spins a dial, which reads as work in progress
                before "Upload and classify" has even been clicked. Show a
                document icon instead; the dial belongs to the real transfer. */}
            {entry.status === 'pending' && (
              <span className="obw-upload-icon">
                {/* Titled, not aria-hidden: this is the row's state indicator,
                    the counterpart to Carbon's own "Uploading file" spinner
                    and "Upload complete" checkmark on the other states. */}
                <Document size={16}>
                  <title>Ready to upload</title>
                </Document>
              </span>
            )}
            <FileUploaderItem
              name={entry.file.name}
              status={
                entry.status === 'done'
                  ? 'complete'
                  : // 'edit' renders the remove button with no dial — right for
                    // a staged file the user may still drop, and for a failed one.
                    entry.status === 'error' || entry.status === 'pending'
                    ? 'edit'
                    : 'uploading'
              }
              invalid={entry.status === 'error'}
              errorSubject={entry.status === 'error' ? 'Upload failed' : undefined}
              // Labels the trailing control. Carbon's default ("Uploading
              // file") is wrong for a staged row, where the control removes
              // the file rather than reporting on a transfer.
              iconDescription={UPLOAD_ICON_DESCRIPTION[entry.status]}
              onDelete={() => onRemoveFile(index)}
            />
          </div>
          {entry.status === 'uploading' && (
            <ProgressBar
              label={`Uploading ${entry.file.name}`}
              hideLabel
              value={entry.progress}
              max={100}
              size="small"
            />
          )}
        </div>
      ))}
    </div>
  );
}
